import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { itemsAPI, batchesAPI } from '../services/api';
import { getFromCache } from '../services/cacheService';
import { toast } from 'react-toastify';
import { getItemName } from '../utils/itemHelpers';
import JsBarcode from 'jsbarcode';
import Navbar from '../components/Navbar';
import './Items.css';
import ItemImageUploader from '../components/ItemImageUploader';

const Items = () => {
  const getInitialBatchForm = () => ({
    batch_code: '',
    currency: 'USD',
    number_of_boxes: 1,
    items_per_box: 1,
    box_buying_price: '',
    box_selling_price: '',
    unit_buying_price: '',
    unit_selling_price: '',
    production_date: '',
    expiry_date: '',
    alert_days: 30
  });

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBatchesModal, setShowBatchesModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemBatches, setItemBatches] = useState({});
  const [editingBatch, setEditingBatch] = useState(null);
  const [batchForm, setBatchForm] = useState(getInitialBatchForm());
  const [isSubmittingBatch, setIsSubmittingBatch] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    name_fr: '',
    description: '',
    barcode: '',
    sku: '',
    currency: 'USD',
    exchange_rate: '',
    buying_price: '',
    selling_price: '',
    unit_type: 'quantity',
    allow_fractional: false,
    is_sold_by_weight: false,  // Keep for backward compatibility
    quantity: 0,
    category: '',
    brand: '',
    low_stock_threshold: 10,
  });

  const { user, logout } = useAuth();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

  const sanitizePriceInput = (value) => {
    if (value === null || value === undefined) return '';
    const cleaned = String(value)
      .replace(/,/g, '')
      .replace(/[^\d.]/g, '');

    const firstDot = cleaned.indexOf('.');
    if (firstDot === -1) return cleaned;

    const beforeDot = cleaned.slice(0, firstDot + 1);
    const afterDot = cleaned.slice(firstDot + 1).replace(/\./g, '');
    return `${beforeDot}${afterDot}`;
  };

  const parsePriceInput = (value) => {
    const sanitized = sanitizePriceInput(value);
    const parsed = parseFloat(sanitized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatPriceInput = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const sanitized = sanitizePriceInput(value);
    if (sanitized === '') return '';

    const hasDot = sanitized.includes('.');
    const [integerPartRaw, decimalPart = ''] = sanitized.split('.');
    const integerPart = integerPartRaw === '' ? '0' : integerPartRaw;
    const formattedInteger = Number(integerPart).toLocaleString('en-US');

    if (hasDot) {
      return `${formattedInteger}.${decimalPart}`;
    }
    return formattedInteger;
  };

  const getHeaders = () => {
    const token = localStorage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  };


  // Estimated sale value: active items + active batches + selling price
  const getTotalValues = () => {
    let totalUSD = 0;
    let totalLBP = 0;

    const activeItemIds = new Set(
      items
        .filter(item => item.is_active !== false)
        .map(item => String(item.id))
    );
    
    Object.keys(itemBatches).forEach(itemId => {
      if (!activeItemIds.has(String(itemId))) {
        return;
      }

      const batches = itemBatches[itemId] || [];
      batches.forEach(batch => {
        if (batch.is_active === false) {
          return;
        }

        const quantity = parseFloat(batch.quantity || 0);
        if (quantity <= 0) {
          return;
        }

        const sellingPrice = parseFloat(batch.unit_selling_price || 0);
        const batchValue = quantity * sellingPrice;
        if (batch.currency === 'USD') {
          totalUSD += batchValue;
        } else if (batch.currency === 'LBP') {
          totalLBP += batchValue;
        }
      });
    });
    
    return { totalUSD, totalLBP };
  };

  const { totalUSD, totalLBP } = getTotalValues();

  const fetchData = async () => {
    try {
      const [itemsRes, categoriesRes, brandsRes] = await Promise.all([
        itemsAPI.getItems({ include_inactive: 1 }).catch(async (error) => {
          if (!navigator.onLine) {
            const cached = await getFromCache('ITEMS');
            if (cached) return { data: cached };
            throw error;
          }
          throw error;
        }),
        itemsAPI.getCategories().catch(async (error) => {
          if (!navigator.onLine) {
            const cached = await getFromCache('CATEGORIES');
            if (cached) return { data: cached };
            throw error;
          }
          throw error;
        }),
        itemsAPI.getBrands().catch(async (error) => {
          if (!navigator.onLine) {
            const cached = await getFromCache('BRANDS');
            if (cached) return { data: cached };
            throw error;
          }
          throw error;
        }),
      ]).catch(async () => {
        // All failed, try cache
        return Promise.all([
          getFromCache('ITEMS').then(d => ({ data: d || [] })),
          getFromCache('CATEGORIES').then(d => ({ data: d || [] })),
          getFromCache('BRANDS').then(d => ({ data: d || [] })),
        ]);
      });

      const fetchedItems = itemsRes.data.results || itemsRes.data || [];
      
      // Filter out invalid items (no name), but keep both active and inactive
      const validItems = fetchedItems.filter(item => 
        item.name 
        && item.name.trim() !== ''
      );
      
      setItems(validItems);
      setCategories(categoriesRes.data.results || categoriesRes.data || []);
      setBrands(brandsRes.data.results || brandsRes.data || []);
      
      // Load batches for all valid items
      console.log('Loading batches for', validItems.length, 'valid items');
      await Promise.all(validItems.map(item => fetchBatches(item.id)));
      console.log('All batches loaded');
      
      // Show offline notification if using cached data
      if (!navigator.onLine) {
        toast.info('Using offline data (cached items)');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBatches = async (itemId) => {
    try {
      const response = await batchesAPI.getBatches({ item: itemId });
      const batches = response.data.results || response.data;
      console.log(`Batches for item ${itemId}:`, batches, 'Type:', Array.isArray(batches) ? 'Array' : typeof batches);
      setItemBatches(prev => {
        const updated = {
          ...prev,
          [itemId]: Array.isArray(batches) ? batches : []
        };
        console.log('Updated itemBatches:', updated);
        return updated;
      });
    } catch (error) {
      console.error(`Error fetching batches for item ${itemId}:`, error.response?.data || error.message);
      setItemBatches(prev => ({
        ...prev,
        [itemId]: []
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const itemPriceFields = ['buying_price', 'selling_price'];
    setFormData({
      ...formData,
      [name]: itemPriceFields.includes(name) ? sanitizePriceInput(value) : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const toNumber = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        const parsed = parseFloat(sanitizePriceInput(value));
        return Number.isFinite(parsed) ? parsed : null;
      };
      const parsedBuying = toNumber(formData.buying_price);
      const parsedSelling = toNumber(formData.selling_price);

      if (parsedBuying !== null && parsedSelling !== null && parsedSelling < parsedBuying) {
        toast.warn(
          t('sellingPriceLowerWarning') ||
          'Warning: Selling price is lower than buying price'
        );
      }

      const itemData = {
        name: formData.name,
        name_ar: formData.name_ar || null,
        name_fr: formData.name_fr || null,
        description: formData.description || null,
        description_ar: formData.description_ar || null,
        description_fr: formData.description_fr || null,
        barcode: formData.barcode || null,
        sku: formData.sku || null,
        currency: formData.currency || 'USD',
        exchange_rate: toNumber(formData.exchange_rate),
        buying_price: parsedBuying ?? 0,
        selling_price: parsedSelling ?? 0,
        unit_type: formData.unit_type || 'quantity',
        allow_fractional: formData.allow_fractional || false,
        is_sold_by_weight: formData.is_sold_by_weight || false,  // Keep for backward compatibility
        quantity: editingItem ? (editingItem.total_quantity ?? editingItem.quantity ?? 0) : 0,
        category: formData.category || null,
        brand: formData.brand || null,
        low_stock_threshold: formData.low_stock_threshold || 10
      };
      if (!editingItem) {
        delete itemData.low_stock_threshold;
      }
      
      if (editingItem) {
        await itemsAPI.updateItem(editingItem.id, itemData);
        toast.success(t('itemUpdatedSuccess'));
      } else {
        await itemsAPI.createItem(itemData);
        toast.success(t('itemAddedSuccess'));
      }

      setShowModal(false);
      resetForm();
      await fetchData();
    } catch (error) {
      console.error('Error:', error);
      const apiMessage = error.response?.data
        ? JSON.stringify(error.response.data)
        : t('error');
      toast.error(apiMessage);
    }
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      name_ar: item.name_ar || '',
      name_fr: item.name_fr || '',
      description: item.description || '',
      barcode: item.barcode || '',
      sku: item.sku || '',
      currency: item.currency || 'USD',
      exchange_rate: item.exchange_rate || '',
      buying_price: item.buying_price,
      selling_price: item.selling_price,
      unit_type: item.unit_type || 'quantity',
      allow_fractional: item.allow_fractional || false,
      is_sold_by_weight: item.is_sold_by_weight || false,  // Keep for backward compatibility
      quantity: item.quantity,
      category: item.category || '',
      brand: item.brand || '',
      low_stock_threshold: item.low_stock_threshold,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('deleteItem') + '?')) {
      try {
        await itemsAPI.deleteItem(id);
        toast.success(t('itemDeletedSuccess'));
        fetchData();
      } catch (error) {
        const errorCode = error.response?.data?.errorCode;
        if (errorCode === 'cannot_delete_item_with_batches') {
          toast.error(t('cannotDeleteItemWithBatches'));
        } else if (errorCode === 'cannot_delete_item_with_sales') {
          toast.error(t('cannotDeleteItemWithSales'));
        } else if (error.response?.status === 400 && error.response?.data?.error) {
          toast.error(error.response.data.error);
        } else {
          toast.error(t('error'));
        }
      }
    }
  };

  const handleToggleActive = async (item) => {
    const totalQuantity = item.total_quantity || 0;
    
    // If trying to deactivate, check if stock is zero
    if (item.is_active && totalQuantity > 0) {
      toast.error(t('cannotDeactivateItemWithStock'));
      return;
    }
    
    const confirmMessage = item.is_active 
      ? t('deactivateItem') + '?' 
      : t('activateItem') + '?';
    
    if (window.confirm(confirmMessage)) {
      try {
        await itemsAPI.updateItem(item.id, {
          ...item,
          is_active: !item.is_active
        });
        
        const successMessage = item.is_active 
          ? t('itemDeactivated') 
          : t('itemActivated');
        
        toast.success(successMessage);
        fetchData();
      } catch (error) {
        console.error('Error toggling item status:', error);
        toast.error(t('error'));
      }
    }
  };

  const handleDeleteBatch = async (batchId, hasSales) => {
    if (hasSales) {
      toast.error(t('cannotDeleteBatchWithSales') || 'Cannot delete batch that has been sold');
      return;
    }
    
    if (window.confirm(t('deleteConfirm') + '?')) {
      try {
        await batchesAPI.deleteBatch(batchId);
        toast.success(t('batchDeletedSuccess'));
        fetchData();
      } catch (error) {
        console.error('Error deleting batch:', error);
        const errorCode = error.response?.data?.errorCode;
        if (errorCode === 'cannot_delete_batch_with_sales') {
          toast.error(t('cannotDeleteBatchWithSales'));
        } else if (error.response?.status === 400 && error.response?.data?.error) {
          toast.error(error.response.data.error);
        } else {
          toast.error(t('error'));
        }
      }
    }
  };
  
  const handleSetPreferredBatch = async (itemId, batchId) => {
    try {
      await itemsAPI.setPreferredBatch(itemId, batchId);
      
      if (batchId === null) {
        toast.success(t('preferredBatchCleared') || 'Now using FEFO logic');
      } else {
        toast.success(t('preferredBatchSet') || 'Preferred batch set');
      }
      
      // Refresh data
      await fetchData();
      // Update selectedItem
      const updatedItem = items.find(i => i.id === itemId);
      if (updatedItem) {
        setSelectedItem(updatedItem);
        await fetchBatches(itemId);
      }
    } catch (error) {
      console.error('Error setting preferred batch:', error);
      toast.error(t('preferredBatchError') || 'Failed to set preferred batch');
    }
  };

  const handlePrintItemBarcode = (item) => {
    const barcodeValue = String(item?.barcode || '').trim();

    if (!barcodeValue) {
      toast.error(t('pleaseEnterBarcode') || 'This item does not have a barcode');
      return;
    }

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    try {
      JsBarcode(svg, barcodeValue, {
        format: 'CODE128',
        displayValue: false,
        margin: 0,
        width: 2,
        height: 72,
        background: '#ffffff',
        lineColor: '#111111'
      });
    } catch (error) {
      console.error('Failed to generate barcode:', error);
      toast.error(t('error') || 'Failed to generate barcode');
      return;
    }

    const printWindow = window.open('', '', 'width=420,height=620');
    if (!printWindow) {
      toast.error(t('error') || 'Unable to open print window');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <title>${t('print') || 'Print'} ${t('barcode') || 'Barcode'}</title>
          <style>
            body {
              margin: 0;
              padding: 24px;
              font-family: Arial, sans-serif;
              background: #ffffff;
              color: #111111;
            }
            .barcode-print {
              display: flex;
              min-height: calc(100vh - 48px);
              align-items: center;
              justify-content: center;
            }
            .barcode-card {
              width: 100%;
              max-width: 320px;
              text-align: center;
              border: 1px solid #dcdcdc;
              border-radius: 12px;
              padding: 24px 18px;
            }
            .barcode-card svg {
              width: 100%;
              height: auto;
            }
            .barcode-number {
              margin-top: 12px;
              font-size: 20px;
              font-weight: 700;
              letter-spacing: 1.5px;
              word-break: break-word;
            }
            .batch-name {
              margin-bottom: 14px;
              font-size: 14px;
              color: #555555;
            }
            @media print {
              body {
                padding: 0;
              }
              .barcode-card {
                border: none;
                border-radius: 0;
                max-width: none;
                padding: 12mm 8mm;
              }
            }
          </style>
        </head>
        <body>
          <div class="barcode-print">
            <div class="barcode-card">
              <div class="batch-name">${item ? getItemName(item, i18n.language) : ''}</div>
              ${svg.outerHTML}
              <div class="barcode-number">${barcodeValue}</div>
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const resetBatchForm = async () => {
    setEditingBatch(null);
    setBatchForm(getInitialBatchForm());
    
    // Auto-generate new batch code when canceling edit
    try {
      const response = await batchesAPI.generateCode();
      setBatchForm(prev => ({
        ...prev,
        batch_code: response.data.batch_code
      }));
    } catch (error) {
      console.error('Error generating batch code:', error);
    }
  };

  const handleEditBatch = (batch) => {
    setEditingBatch(batch);
    setBatchForm({
      batch_code: batch.batch_code || '',
      currency: batch.currency || 'USD',
      number_of_boxes: batch.number_of_boxes || 1,
      items_per_box: batch.items_per_box || 1,
      box_buying_price: batch.box_buying_price || '',
      box_selling_price: batch.box_selling_price || '',
      unit_buying_price: batch.unit_buying_price || '',
      unit_selling_price: batch.unit_selling_price || '',
      production_date: batch.production_date || '',
      expiry_date: batch.expiry_date || '',
      alert_days: batch.alert_days || 30
    });
    // Scroll to form at top of modal
    setTimeout(() => {
      const modal = document.querySelector('.batches-modal');
      if (modal) {
        modal.scrollTop = 0;
      }
    }, 100);
  };

  const handleOpenBatches = async (item) => {
    setSelectedItem(item);
    setEditingBatch(null);
    setBatchForm(getInitialBatchForm());
    await fetchBatches(item.id);
    
    // Auto-generate batch code for new batch
    try {
      const response = await batchesAPI.generateCode();
      setBatchForm(prev => ({
        ...getInitialBatchForm(),
        batch_code: response.data.batch_code
      }));
    } catch (error) {
      console.error('Error generating batch code:', error);
      // Continue without auto-generated code
    }
    
    setShowBatchesModal(true);
  };

  const handleBatchInputChange = (field, value) => {
    setBatchForm((prev) => {
      const priceFields = ['box_buying_price', 'box_selling_price', 'unit_buying_price', 'unit_selling_price'];
      const normalizedValue = priceFields.includes(field) ? sanitizePriceInput(value) : value;
      const updated = { ...prev, [field]: normalizedValue };
      
      // Only auto-calculate unit prices when box prices or items_per_box change
      // This allows manual editing of unit prices
      if (['box_buying_price', 'box_selling_price', 'items_per_box'].includes(field)) {
        const boxBuyingPrice = parsePriceInput(updated.box_buying_price);
        const boxSellingPrice = parsePriceInput(updated.box_selling_price);
        const itemsPerBox = parseInt(updated.items_per_box) || 1;

        if (itemsPerBox > 0) {
          updated.unit_buying_price = (boxBuyingPrice / itemsPerBox).toFixed(2);
          updated.unit_selling_price = (boxSellingPrice / itemsPerBox).toFixed(2);
        }
      }

      return updated;
    });
  };

  const handleAddBatch = async (e) => {
    e.preventDefault();
    if (!selectedItem?.id || isSubmittingBatch) return;

    const productionDate = batchForm.production_date ? new Date(batchForm.production_date) : null;
    const expiryDate = batchForm.expiry_date ? new Date(batchForm.expiry_date) : null;

    if (productionDate && expiryDate && expiryDate <= productionDate) {
      toast.warn(
        t('expiryBeforeProductionError') ||
        'Expiry date must be after production date'
      );
      return;
    }

    setIsSubmittingBatch(true);

    try {
      const currentItemId = selectedItem.id;
      const batchData = {
        item: currentItemId,
        batch_code: batchForm.batch_code,
        currency: batchForm.currency || 'USD',
        number_of_boxes: parseInt(batchForm.number_of_boxes) || 1,
        items_per_box: parseInt(batchForm.items_per_box) || 1,
        box_buying_price: parsePriceInput(batchForm.box_buying_price),
        box_selling_price: parsePriceInput(batchForm.box_selling_price),
        unit_buying_price: parsePriceInput(batchForm.unit_buying_price),
        unit_selling_price: parsePriceInput(batchForm.unit_selling_price),
        production_date: batchForm.production_date,
        expiry_date: batchForm.expiry_date,
        alert_days: parseInt(batchForm.alert_days || 30)
      };

      if (editingBatch) {
        // Update existing batch
        await batchesAPI.updateBatch(editingBatch.id, batchData);
        toast.success(t('batchUpdatedSuccess') || 'Batch updated successfully');
      } else {
        // Create new batch
        await batchesAPI.createBatch(batchData);
        toast.success(t('batchAddedSuccess') || 'Batch added successfully');
      }
      
      await fetchData();
      await fetchBatches(currentItemId);
      setShowBatchesModal(false);
      setSelectedItem(null);
      await resetBatchForm();
    } catch (error) {
      console.error('Error adding batch:', error);
      toast.error(error.response?.data ? JSON.stringify(error.response.data) : t('error'));
    } finally {
      setIsSubmittingBatch(false);
    }
  };

  const handleCloseBatchesModal = () => {
    setShowBatchesModal(false);
    setSelectedItem(null);
    resetBatchForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      name_ar: '',
      name_fr: '',
      description: '',
      barcode: '',
      sku: '',
      currency: 'USD',
      exchange_rate: '',
      buying_price: '',
      selling_price: '',
      quantity: 0,
      category: '',
      brand: '',
      low_stock_threshold: 10,
    });
    setEditingItem(null);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === parseInt(categoryId));
    if (!category) return '';
    if (i18n.language === 'ar') return category.name_ar || category.name;
    if (i18n.language === 'fr') return category.name_fr || category.name;
    return category.name;
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => b.id === parseInt(brandId));
    if (!brand) return '';
    if (i18n.language === 'ar') return brand.name_ar || brand.name;
    if (i18n.language === 'fr') return brand.name_fr || brand.name;
    return brand.name;
  };

  const filteredItems = items.filter((item) => 
    item.is_active !== false &&
    (getItemName(item, i18n.language).toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.barcode && item.barcode.includes(searchTerm)) ||
    (item.category && getCategoryName(item.category).toLowerCase().includes(searchTerm.toLowerCase())))
  );

  const frozenItems = items.filter((item) => 
    item.is_active === false &&
    (getItemName(item, i18n.language).toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.barcode && item.barcode.includes(searchTerm)) ||
    (item.category && getCategoryName(item.category).toLowerCase().includes(searchTerm.toLowerCase())))
  );

  const sortedSelectedItemBatches = selectedItem?.id
    ? [...(itemBatches[selectedItem.id] || [])].sort((a, b) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        if (aTime !== bTime) return bTime - aTime;
        return (b.id || 0) - (a.id || 0);
      })
    : [];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return <div className="loading">{t('loading')}</div>;
  }

  return (
    <div className="items-page">
      <Navbar />
      
      <div className="items-content" style={{ padding: '20px' }}>
        <header className="items-header">
          <div className="header-left">
            <div>
              <h1>{t('items')}</h1>
              <p>{user?.market_name}</p>
            </div>
          </div>
          <div className="header-actions"></div>
        </header>

      <div className="items-controls">
        <input
          type="text"
          placeholder={t('search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn btn-primary"
        >
          + {t('addItem')}
        </button>
      </div>
      
      <div style={{ padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'center', minWidth: '200px', flex: '1' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2c3e50', wordBreak: 'break-word', overflowWrap: 'break-word', padding: '5px' }}>{t('usdSymbol')}{totalUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div style={{ color: '#7f8c8d' }}>{t('totalValueSelling') || t('totalValue')} ({t('usd')})</div>
        </div>
        <div style={{ borderLeft: '2px solid #ddd' }}></div>
        <div style={{ textAlign: 'center', minWidth: '200px', flex: '1' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2c3e50', wordBreak: 'break-word', overflowWrap: 'break-word', padding: '5px' }}>{totalLBP.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('lbpSymbol')}</div>
          <div style={{ color: '#7f8c8d' }}>{t('totalValueSelling') || t('totalValue')} ({t('lbp')})</div>
        </div>
      </div>

      <div className="items-grid">
        {filteredItems.map((item) => (
          <div key={item.id} className="item-card item-card-clickable" onClick={() => handleOpenBatches(item)}>
            <div className="item-header">
              <h3>{getItemName(item, i18n.language)}</h3>
              {item.is_low_stock && <span className="badge-warning">Low Stock</span>}
            </div>
            
            <div className="item-details">
              {item.barcode && <p><strong>{t('barcode')}:</strong> {item.barcode}</p>}
              {item.category && <p><strong>{t('category')}:</strong> {item.category}</p>}
              <p><strong>{t('totalQuantity')}:</strong> {(item.total_quantity || 0).toLocaleString('en-US')} {t('items')}</p>
              <p><strong>{t('numberOfBatches')}:</strong> {itemBatches[item.id]?.length || 0}</p>
            </div>

            <div className="item-actions">
              <button onClick={(e) => { e.stopPropagation(); handleEdit(item); }} className="btn-edit">
                {t('edit')}
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); handleToggleActive(item); }} 
                className={`btn-toggle ${!item.is_active ? 'inactive' : ''}`}
                title={item.is_active ? t('deactivateItem') : t('activateItem')}
              >
                {item.is_active ? '❄️' : '✓'}
              </button>
              <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} className="btn-delete">
                {t('delete')}
              </button>
            </div>
            {item.barcode && (
              <div className="item-secondary-actions">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handlePrintItemBarcode(item); }}
                  className="btn-print-barcode"
                >
                  🏷️ {(t('print') || 'Print')} {(t('barcode') || 'Barcode')}
                </button>
              </div>
            )}
            <div className="item-open-hint">{t('batches')} ↓</div>
          </div>
        ))}
      </div>

      {/* Frozen Items Section */}
      {frozenItems.length > 0 && (
        <div className="frozen-items-section">
          <h2>{t('frozenItems') || 'Frozen Items'}</h2>
          <div className="items-grid">
            {frozenItems.map((item) => (
              <div key={item.id} className="item-card item-card-frozen item-card-clickable" onClick={() => handleOpenBatches(item)}>
                <div className="item-header">
                  <h3>{getItemName(item, i18n.language)}</h3>
                </div>
                
                <div className="item-details">
                  {item.barcode && <p><strong>{t('barcode')}:</strong> {item.barcode}</p>}
                  {item.category && <p><strong>{t('category')}:</strong> {item.category}</p>}
                  <p><strong>{t('totalQuantity')}:</strong> {(item.total_quantity || 0).toLocaleString('en-US')} {t('items')}</p>
                  <p><strong>{t('numberOfBatches')}:</strong> {itemBatches[item.id]?.length || 0}</p>
                </div>

                <div className="item-actions">
                  <button onClick={(e) => { e.stopPropagation(); handleEdit(item); }} className="btn-edit">
                    {t('edit')}
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleToggleActive(item); }} 
                    className={`btn-toggle ${!item.is_active ? 'inactive' : ''}`}
                    title={item.is_active ? t('deactivateItem') : t('activateItem')}
                  >
                    {'✓'}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} className="btn-delete">
                    {t('delete')}
                  </button>
                </div>
                {item.barcode && (
                  <div className="item-secondary-actions">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handlePrintItemBarcode(item); }}
                      className="btn-print-barcode"
                    >
                      🏷️ {(t('print') || 'Print')} {(t('barcode') || 'Barcode')}
                    </button>
                  </div>
                )}
                <div className="item-open-hint">{t('batches')} ↓</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingItem ? t('editItem') : t('addItem')}</h2>
            <form onSubmit={handleSubmit} className="item-form">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('itemName')} (EN)*</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>{t('itemName')} (AR)</label>
                  <input
                    type="text"
                    name="name_ar"
                    value={formData.name_ar}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('itemName')} (FR)</label>
                  <input
                    type="text"
                    name="name_fr"
                    value={formData.name_fr}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>{t('category')}</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="">{t('selectCategory')}</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {i18n.language === 'ar' ? (cat.name_ar || cat.name) : i18n.language === 'fr' ? (cat.name_fr || cat.name) : cat.name}
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#3498db', fontSize: '12px', cursor: 'pointer' }}>
                    <a href="/categories-brands" style={{ color: '#3498db', textDecoration: 'none' }}>
                      {t('manageCategoriesAndBrands')}
                    </a>
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('brand')}</label>
                  <select
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="">{t('selectBrand')}</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {i18n.language === 'ar' ? (brand.name_ar || brand.name) : i18n.language === 'fr' ? (brand.name_fr || brand.name) : brand.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('barcode')}</label>
                  <input
                    type="text"
                    name="barcode"
                    value={formData.barcode}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('lowStockThreshold')}</label>
                  <input
                    type="number"
                    name="low_stock_threshold"
                    value={formData.low_stock_threshold}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>{t('sku')}</label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('buyingPrice')}*</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    name="buying_price"
                    value={formatPriceInput(formData.buying_price)}
                    onChange={handleChange}
                    required
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>{t('sellingPrice')}*</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    name="selling_price"
                    value={formatPriceInput(formData.selling_price)}
                    onChange={handleChange}
                    required
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('unitType')}</label>
                  <select
                    name="unit_type"
                    value={formData.unit_type}
                    onChange={(e) => setFormData({ ...formData, unit_type: e.target.value })}
                    className="form-control"
                  >
                    <option value="quantity">{t('unitTypeQuantity')}</option>
                    <option value="weight">{t('unitTypeWeight')}</option>
                    <option value="length">{t('unitTypeLength')}</option>
                    <option value="area">{t('unitTypeArea')}</option>
                    <option value="volume">{t('unitTypeVolume')}</option>
                  </select>
                  <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                    {t('unitTypeHint')}
                  </small>
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="allow_fractional"
                      checked={formData.allow_fractional}
                      onChange={(e) => setFormData({ ...formData, allow_fractional: e.target.checked })}
                      style={{ marginRight: '8px', marginLeft: '8px' }}
                    />
                    {t('allowFractional')}
                  </label>
                  <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                    {t('allowFractionalHint')}
                  </small>
                </div>
              </div>

              {editingItem && editingItem.id && (
                <div className="form-group">
                  <label>{t('addImages')}</label>
                  <ItemImageUploader
                    itemId={editingItem.id}
                    onImagesUpdate={() => fetchData()}
                  />
                </div>
              )}

              <div className="form-group">
                <label>{t('description')}</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="form-control"
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">
                  {t('save')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                >
                  {t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBatchesModal && selectedItem && (
        <div className="modal-overlay" onClick={handleCloseBatchesModal}>
          <div className="modal-content batches-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('batches')} - {getItemName(selectedItem, i18n.language)}</h2>

            <div className="form-section batch-entry-top">
              <div className="section-header">
                <h3>{editingBatch ? (t('editBatch') || 'Edit Batch') : t('addBatch')}</h3>
              </div>

              <form onSubmit={handleAddBatch}>
                <div className="batch-form-fields">
                  <div className="form-group">
                    <label>{t('batchCode')}*</label>
                    <input
                      type="text"
                      value={batchForm.batch_code}
                      onChange={(e) => handleBatchInputChange('batch_code', e.target.value)}
                      placeholder="LOT-001"
                      required
                      readOnly={!!editingBatch}
                      disabled={!!editingBatch}
                      className="form-control"
                      style={editingBatch ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
                    />
                    {editingBatch && (
                      <small style={{ color: '#666', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
                        {t('cannotEditBatchCode') || 'Batch code cannot be modified'}
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>{t('numberOfBoxes')}*</label>
                    <input
                      type="number"
                      value={batchForm.number_of_boxes}
                      onChange={(e) => handleBatchInputChange('number_of_boxes', e.target.value)}
                      min="1"
                      required
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('itemsPerBox')}*</label>
                    <input
                      type="number"
                      value={batchForm.items_per_box}
                      onChange={(e) => handleBatchInputChange('items_per_box', e.target.value)}
                      min="1"
                      required
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('currency')}*</label>
                    <select
                      value={batchForm.currency || 'USD'}
                      onChange={(e) => handleBatchInputChange('currency', e.target.value)}
                      required
                      className="form-control"
                    >
                      <option value="USD">{t('usd')}</option>
                      <option value="LBP">{t('lbp')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('boxBuyingPrice')}*</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formatPriceInput(batchForm.box_buying_price)}
                      onChange={(e) => handleBatchInputChange('box_buying_price', e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('boxSellingPrice')}*</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formatPriceInput(batchForm.box_selling_price)}
                      onChange={(e) => handleBatchInputChange('box_selling_price', e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('unitBuyingPrice')} *</label>
                    <input 
                      type="text"
                      inputMode="decimal"
                      value={formatPriceInput(batchForm.unit_buying_price)}
                      onChange={(e) => handleBatchInputChange('unit_buying_price', e.target.value)}
                      required
                      className="form-control" 
                    />
                    <small style={{ color: '#666', fontSize: '0.85em' }}>{t('autoCalculatedFromBox') || 'Auto-calculated from box price, can be edited'}</small>
                  </div>
                  <div className="form-group">
                    <label>{t('unitSellingPrice')} *</label>
                    <input 
                      type="text"
                      inputMode="decimal"
                      value={formatPriceInput(batchForm.unit_selling_price)}
                      onChange={(e) => handleBatchInputChange('unit_selling_price', e.target.value)}
                      required
                      className="form-control" 
                    />
                    <small style={{ color: '#666', fontSize: '0.85em' }}>{t('autoCalculatedFromBox') || 'Auto-calculated from box price, can be edited'}</small>
                  </div>
                  <div className="form-group">
                    <label>{t('productionDate')}*</label>
                    <input
                      type="date"
                      value={batchForm.production_date}
                      onChange={(e) => handleBatchInputChange('production_date', e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('expiryDate')}*</label>
                    <input
                      type="date"
                      value={batchForm.expiry_date}
                      onChange={(e) => handleBatchInputChange('expiry_date', e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('alertDays')}</label>
                    <input
                      type="number"
                      value={batchForm.alert_days}
                      onChange={(e) => handleBatchInputChange('alert_days', e.target.value)}
                      min="1"
                      max="365"
                      className="form-control"
                    />
                  </div>
                  <div className="form-group batch-quantity-display">
                    <label>{t('totalBatchQuantity')}</label>
                    <div className="calculated-value">
                      {(parseInt(batchForm.number_of_boxes) || 0) * (parseInt(batchForm.items_per_box) || 0)} {t('items')}
                    </div>
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary" disabled={isSubmittingBatch}>
                    {isSubmittingBatch ? (t('loading') || 'Loading...') : (editingBatch ? t('updateBatch') || 'Update Batch' : t('addBatch'))}
                  </button>
                  {editingBatch && (
                    <button type="button" onClick={resetBatchForm} className="btn btn-secondary">
                      {t('cancelEdit') || 'Cancel Edit'}
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h3>{t('batches')} ({sortedSelectedItemBatches.length})</h3>
              </div>
              {sortedSelectedItemBatches.length === 0 ? (
                <p className="no-batches-message">{t('noBatches')}</p>
              ) : (
                <div className="batches-list">
                  {sortedSelectedItemBatches.map((batch) => {
                    const isActiveBatch = selectedItem && selectedItem.latest_batch_id === batch.id;
                    const isPreferred = selectedItem && selectedItem.preferred_batch === batch.id;
                    
                    return (
                    <div key={batch.id} className={`batch-item ${isActiveBatch ? 'active-batch' : ''} ${isPreferred ? 'preferred-batch' : ''}`}>
                      <div className="batch-info">
                        <div className="batch-header-row">
                          <div className="batch-code">
                            {batch.batch_code || '-'}
                            {isActiveBatch && <span className="active-indicator">● {t('activeBatch') || 'Active'}</span>}
                            {isPreferred && <span className="preferred-indicator">⭐ {t('preferredBatch') || 'Preferred'}</span>}
                          </div>
                        </div>
                        <div className="batch-details">
                          <span>{batch.number_of_boxes || 0} {t('boxes')} × {batch.items_per_box || 0} {t('itemsPerBox')}</span>
                          <span>{t('quantity')}: {parseFloat(batch.quantity || 0).toFixed(1)}</span>
                          <span>{batch.currency || 'USD'} {parseFloat(batch.unit_selling_price || 0).toFixed(2)}/{t('unit')}</span>
                          <span>{t('expiryDate')}: {batch.expiry_date ? new Date(batch.expiry_date).toLocaleDateString() : '-'}</span>
                        </div>
                      </div>
                      <div className="batch-actions">
                        {!isPreferred && batch.quantity > 0 && (
                          <button
                            type="button"
                            className="batch-btn-set-preferred"
                            onClick={() => handleSetPreferredBatch(selectedItem.id, batch.id)}
                            title={t('setAsPreferred') || 'Set as preferred batch'}
                          >
                            ⭐ {t('setPreferred') || 'Set Preferred'}
                          </button>
                        )}
                        {isPreferred && (
                          <button
                            type="button"
                            className="batch-btn-clear-preferred"
                            onClick={() => handleSetPreferredBatch(selectedItem.id, null)}
                            title={t('clearPreferred') || 'Clear preferred (use FEFO)'}
                          >
                            🔄 {t('clearPreferred') || 'Use FEFO'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="batch-btn-edit"
                          onClick={() => handleEditBatch(batch)}
                          title={t('edit')}
                        >
                          ✏️ {t('edit')}
                        </button>
                        <button
                          type="button"
                          className="batch-btn-delete"
                          onClick={() => handleDeleteBatch(batch.id, batch.has_sales)}
                          disabled={batch.has_sales}
                        >
                          🗑️ {t('delete')}
                        </button>
                      </div>
                    </div>
                  );})}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button type="button" onClick={handleCloseBatchesModal} className="btn btn-secondary">{t('close') || 'Close'}</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Items;
